import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import toastr from "toastr";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import {
  extractAdminRefundRows,
  extractAdminRefundTotal,
  useAdminRefundApprovalMutation,
  useAdminRefundListQuery,
} from "../../../api/refund-admin";
import { formatGbp } from "../../../helpers/currency";
import { mediaUrl } from "../../../helpers/mediaHelper";

const REFUND_STATUS_COLOR = {
  pending: "warning",
  processing: "primary",
  completed: "success",
  rejected: "danger",
  cancelled: "secondary",
};

function clientLabel(row) {
  const user = row?.user;
  if (!user) return "—";
  return user.fullName || user.email || "—";
}

export default function AdminRefundListPage() {
  document.title = "Refund requests | Admin";

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [reviewRow, setReviewRow] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [proofFile, setProofFile] = useState(null);

  const listParams = useMemo(() => ({ page, limit }), [page, limit]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminRefundListQuery(listParams);
  const approvalMutation = useAdminRefundApprovalMutation();

  const rows = extractAdminRefundRows(data);
  const total = extractAdminRefundTotal(data);

  const closeReview = useCallback(() => {
    setReviewRow(null);
    setShowRejectForm(false);
    setRejectReason("");
    setProofFile(null);
  }, []);

  const reviewSlug = reviewRow?.slug ?? "";
  const existingProofUrl = reviewRow?.proof
    ? mediaUrl(reviewRow.proof)
    : "";
  const canDecide =
    reviewRow?.status === "pending" && reviewRow?.isVerified === true;

  const columns = useMemo(
    () => [
      {
        header: "Refund ID",
        accessorKey: "slug",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "—",
      },
      {
        header: "Client",
        accessorKey: "user.fullName",
        enableColumnFilter: false,
        cell: (info) => {
          const user = info.row.original.user;
          if (!user) return "—";
          return (
            <div>
              <div className="fw-semibold">{user.fullName || "—"}</div>
              <small className="text-muted">{user.email || ""}</small>
            </div>
          );
        },
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: (info) => formatGbp(info.getValue()),
      },
      {
        header: "To refund",
        accessorKey: "amountToRefund",
        enableColumnFilter: false,
        cell: (info) => {
          const value = info.getValue();
          return value != null && value !== "" ? formatGbp(value) : "—";
        },
      },
      {
        header: "Bank",
        accessorKey: "bankName",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "—",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const statusValue = info.getValue();
          const color = REFUND_STATUS_COLOR[statusValue] || "info";
          return (
            <Badge className={`bg-${color} text-uppercase`}>
              {statusValue || "unknown"}
            </Badge>
          );
        },
      },
      {
        header: "Verified",
        accessorKey: "isVerified",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue() ? (
            <Badge className="bg-success">Yes</Badge>
          ) : (
            <Badge className="bg-warning text-dark">No</Badge>
          ),
      },
      {
        header: "Requested",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? dayjs(info.getValue()).format("DD MMM YYYY HH:mm")
            : "—",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          if (!row.slug) return "—";
          return (
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setReviewRow(row);
                setShowRejectForm(false);
                setRejectReason("");
                setProofFile(null);
              }}
            >
              Review
            </Button>
          );
        },
      },
    ],
    []
  );

  const submitComplete = () => {
    if (!reviewSlug) return;
    if (!proofFile) {
      toastr.error("Please upload proof of refund.");
      return;
    }
    approvalMutation.mutate(
      { slug: reviewSlug, status: "completed", proof: proofFile },
      {
        onSuccess: () => {
          toastr.success("Refund marked as completed.");
          closeReview();
          refetch();
        },
      }
    );
  };

  const submitReject = () => {
    if (!reviewSlug) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toastr.error("Please enter a rejection reason.");
      return;
    }
    approvalMutation.mutate(
      { slug: reviewSlug, status: "rejected", rejectionReason: reason },
      {
        onSuccess: () => {
          toastr.success("Refund request rejected.");
          closeReview();
          refetch();
        },
      }
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Payments" breadcrumbItem="Refund requests" />

        {isError ? (
          <Alert color="danger" className="mb-3">
            {error?.message || "Could not load refund requests."}{" "}
            <Button
              color="link"
              className="p-0 align-baseline"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </Alert>
        ) : null}

        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            {isFetching && !isPending ? (
              <div className="text-end small text-muted mb-2">
                <i className="bx bx-loader-alt bx-spin me-1" />
                Updating…
              </div>
            ) : null}

            {isPending && rows.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : rows.length === 0 ? (
              <div className="text-muted text-center py-4">
                No refund requests found.
              </div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={rows}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />
                {total > 0 ? (
                  <Paginations
                    total={total}
                    perPageData={limit}
                    currentPage={page}
                    setCurrentPage={setPage}
                    isShowingPageLength
                    paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                    paginationClass="pagination"
                  />
                ) : null}
              </>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={Boolean(reviewRow)}
          toggle={closeReview}
          centered
          size="lg"
        >
          <ModalHeader toggle={closeReview}>Review refund request</ModalHeader>
          <ModalBody>
            {reviewRow ? (
              <>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">{reviewRow.slug || "—"}</h5>
                    <div className="text-muted small">
                      Client:{" "}
                      <span className="text-body">{clientLabel(reviewRow)}</span>
                      {reviewRow.user?.email ? (
                        <>
                          {" "}
                          ·{" "}
                          <a href={`mailto:${reviewRow.user.email}`}>
                            {reviewRow.user.email}
                          </a>
                        </>
                      ) : null}
                    </div>
                    {reviewRow.user?.slug ? (
                      <div className="small mt-1">
                        <Link
                          to={`/user/user-details/client/${reviewRow.user.slug}`}
                          className="text-primary"
                        >
                          View client profile
                        </Link>
                      </div>
                    ) : null}
                  </div>
                  <Badge
                    className={`bg-${REFUND_STATUS_COLOR[reviewRow.status] || "info"} text-uppercase`}
                  >
                    {reviewRow.status || "unknown"}
                  </Badge>
                </div>

                <Row className="g-3 mb-3">
                  <Col sm={6}>
                    <p className="text-muted small mb-1">Requested amount</p>
                    <p className="fw-semibold mb-0">
                      {formatGbp(reviewRow.amount)}
                    </p>
                  </Col>
                  <Col sm={6}>
                    <p className="text-muted small mb-1">Amount to refund</p>
                    <p className="fw-semibold mb-0">
                      {reviewRow.amountToRefund != null &&
                      reviewRow.amountToRefund !== ""
                        ? formatGbp(reviewRow.amountToRefund)
                        : "—"}
                    </p>
                  </Col>
                </Row>

                <div className="border rounded p-3 mb-3 bg-light">
                  <h6 className="text-muted mb-2">Bank details</h6>
                  <p className="mb-1 small">
                    <span className="text-muted">Account holder: </span>
                    {reviewRow.accountHolderName || "—"}
                  </p>
                  <p className="mb-1 small">
                    <span className="text-muted">Bank: </span>
                    {reviewRow.bankName || "—"}
                  </p>
                  <p className="mb-1 small">
                    <span className="text-muted">Sort code: </span>
                    {reviewRow.sortCode || "—"}
                  </p>
                  <p className="mb-0 small">
                    <span className="text-muted">Account number: </span>
                    {reviewRow.accountNumber || "—"}
                  </p>
                </div>

                {reviewRow.rejectionReason ? (
                  <Alert color="warning" className="py-2 small">
                    <strong>Rejection reason:</strong> {reviewRow.rejectionReason}
                  </Alert>
                ) : null}

                <div className="mb-3">
                  <h6 className="text-muted mb-2">Proof of refund</h6>
                  {existingProofUrl ? (
                    <a
                      href={existingProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-soft-primary"
                    >
                      <i className="bx bx-link-external me-1" />
                      View uploaded proof
                    </a>
                  ) : (
                    <p className="text-muted small mb-0">
                      No proof uploaded yet.
                    </p>
                  )}
                </div>

                {reviewRow.status === "pending" && !reviewRow.isVerified ? (
                  <Alert color="info" className="small py-2 mb-3">
                    This request is awaiting client email verification before
                    you can approve or reject it.
                  </Alert>
                ) : null}

                {canDecide && !showRejectForm ? (
                  <div className="mb-0">
                    <Label className="form-label" for="refund-proof-upload">
                      Upload proof (required to complete)
                    </Label>
                    <Input
                      id="refund-proof-upload"
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => {
                        setProofFile(e.target.files?.[0] ?? null);
                      }}
                    />
                    {proofFile ? (
                      <small className="text-muted d-block mt-1">
                        Selected: {proofFile.name}
                      </small>
                    ) : null}
                  </div>
                ) : null}

                {showRejectForm ? (
                  <div className="border rounded p-3 bg-light mt-3">
                    <Label className="form-label">Rejection reason</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Explain why this refund is rejected…"
                    />
                  </div>
                ) : null}
              </>
            ) : null}
          </ModalBody>
          <ModalFooter className="flex-wrap gap-2">
            <Button color="light" onClick={closeReview}>
              Close
            </Button>
            {canDecide && !showRejectForm ? (
              <Button
                color="danger"
                outline
                disabled={approvalMutation.isPending}
                onClick={() => {
                  setRejectReason("");
                  setShowRejectForm(true);
                }}
              >
                Reject
              </Button>
            ) : null}
            {showRejectForm ? (
              <>
                <Button
                  color="light"
                  onClick={() => setShowRejectForm(false)}
                >
                  Cancel rejection
                </Button>
                <Button
                  color="danger"
                  disabled={approvalMutation.isPending}
                  onClick={submitReject}
                >
                  {approvalMutation.isPending ? (
                    <>
                      <i className="bx bx-loader-alt bx-spin me-1" />
                      Saving…
                    </>
                  ) : (
                    "Submit rejection"
                  )}
                </Button>
              </>
            ) : null}
            {canDecide && !showRejectForm ? (
              <Button
                color="success"
                disabled={approvalMutation.isPending}
                onClick={submitComplete}
              >
                {approvalMutation.isPending ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin me-1" />
                    Saving…
                  </>
                ) : (
                  "Mark as completed"
                )}
              </Button>
            ) : null}
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
}
